import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import { compact, cloneDeep, isEmpty } from 'lodash';
import {
  getUserData,
  setEcap,
  getEcap,
  saveEcap,
  getData,
  getCurrentUserId,
} from '../../store/actions';
import {
  getOnboardingState,
  getAuthState,
  getEcapState,
} from '../../store/selectors';
import LoginButton from '../utility/LoginButton';
import RouteBack from '../utility/RouteBack';
import SchoolLogo from '../utility/SchoolLogo';
import CareerPathTabs from './CareerPathTabs';
import ReplaceCareerLink from './utility/ReplaceCareerLink';
import CareerPathList from './CareerPathList';
import ProgressBar from '../utility/ProgressBar';
import Loading from '../utility/Loading';
import ModalController from '../utility/modal/ModalController';
import { getSubjectObj } from '../ecap/classes/drag-drop/dragDropUtil';
import { fireGTM } from '../../gtm/gtmUtil';
import { nextButton_GTM } from '../../gtm/datalayers/utilDataLayers.json';
import ScrollComponent from '../utility/ScrollComponent';
import BrightOutlookModal from '../utility/modal/BrightOutlookModal.jsx';
import CareerPathsBar from './utility/CareerPathsBar';
import UpdateDataBanner from '../UpdateDataBanner';
import WindowTitle from '../utility/WindowTitle';

const bars = {
  Onboarding: {
    current: 100,
    override: false,
  },
  'Picture Game': {
    current: 100,
    override: false,
  },
  'Career Path': {
    current: 65,
    override: true,
  },
};

class CareerPath extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.careerPathRef = React.createRef();
    this.state = {
      openList: false,
      loading: true,
      width: null,
      userId: null,
      showBrightOutlookModal: false,
      outOfDateData: false,
    };
  }

  updateWindowDimensions;

  async componentDidMount() {
    this._isMounted = true;
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);

    const userId = await this.props.getCurrentUserId(this.props.auth.jwtToken);
    if (userId) {
      await this.props.getData(userId, this.props.auth.jwtToken);
      await this.props.getUserData(userId, this.props.auth.jwtToken);
    }

    if (this._isMounted) {
      this.setState({
        loading: false,
        userId: this.props.onboarding.userId,
        outOfDateData: this.props.onboarding.userData.outOfDateData,
      });
    }
  }

  updateWindowDimensions = () => {
    if (this._isMounted) this.setState({ width: window.innerWidth });
  };

  async componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  // TODO: Dynamically change percentage
  progressPercentage = 65;

  borderTopSecondary = {
    color: `${this.props.tenant.secondary}`,
  };

  degreePathsSelectedBarSecondary = {
    background: `${this.props.tenant.secondary}`,
  };

  headerHorizontalLineSecondary = {
    borderBottom: `3px solid ${this.props.tenant.secondary}`,
  };

  moveDown = {
    marginTop: '50px',
  };

  get totalPaths() {
    return 3;
  }

  // How many degree-pairings does the user have?
  // Since the number of pairings is used in multiple components, leave this logic in the redux store \
  // instead of setting it in state and trying to pass it around
  get chosenPaths() {
    return compact(Object.values(this.props.onboarding.degreeChoices)).length;
  }

  // Make sure the user's ecap.plan.classes_matched{} is instantiated for each acad_plan
  verifyEcapPlan = async () => {
    const tempEcapPlan = cloneDeep(this.props.ecap.plan);

    // Fix ecap.plan properties to be null if still ''
    Object.entries(tempEcapPlan).forEach(([ecapKey, ecapValue]) => {
      if (ecapValue === '') {
        tempEcapPlan[ecapKey] = null;
      }
    });

    /* eslint-disable no-param-reassign */
    const allClassesMatched = Object.values(
      this.props.onboarding.degreeChoices
    ).reduce((classesMatched) => {
      if (
        this.props.ecap.plan.classes_matched === undefined ||
        isEmpty(this.props.ecap.plan.classes_matched)
      ) {
        const subjectObj = getSubjectObj();

        classesMatched = subjectObj.subjectSlotsObj;
      }

      return classesMatched;
    }, {});
    // just need 1 object to hold all of the array of classes for the drag and drop grid
    const combinedEcapPlans = Object.assign(
      tempEcapPlan.classes_matched,
      allClassesMatched
    );

    tempEcapPlan.classes_matched = combinedEcapPlans;
    this.props.setEcap(tempEcapPlan);
    await this.props.saveEcap(this.props.auth.jwtToken, tempEcapPlan, true);
  };

  handleNextButtonClicked = async (nextPath) => {
    // set the ecap plan for this user
    await this.props.getEcap(this.props.auth.jwtToken);
    this.verifyEcapPlan();

    const { history } = this.props;

    fireGTM(nextButton_GTM, 'next', "now let's build your career path");
    if (this.props.tenant.name === 'InStride') {
      window.location.replace(nextPath);
    } else if (history) {
      history.push(nextPath);
    }
  };

  // If a career tab is expanded, don't show the Progress Bar
  isTabExpanded = () => {
    return (
      Object.values(this.props.onboarding.careerCardsExpanded).indexOf(true) >
      -1
    );
  };

  isListOpen = () => {
    if (this.props.onboarding.careerTabSelected) {
      return (
        <div
          className="desktop-career-list-container"
          key={this.props.onboarding.careerTabSelected}
        >
          <CareerPathList tenant={this.props.tenant} />
        </div>
      );
    }

    if (this.state.openList === true) {
      return this.setState((prevState) => {
        return {
          ...prevState,
          openList: false,
        };
      });
    }
  };

  displayMobileDescription = (t) => {
    return this.state.width < 985
      ? t(
          'Click on the Build path links below to explore degrees and credentials that match the careers you selected.'
        )
      : t(
          'Click on the Build your career path links below to explore degrees and credentials that match the careers you selected.'
        );
  };

  createGTMCareerPaths = () => {
    const preferences = {};

    Object.values(this.props.onboarding.degreeChoices).forEach(
      (value, index) => {
        preferences[index] = value.titleEn;
      }
    );

    return preferences;
  };

  setBrightOutlookModal = () => {
    this.setState((prevState) => ({
      showBrightOutlookModal: !prevState.showBrightOutlookModal,
    }));
  };

  // How many degree-pairings does the user have?
  checkPathsChosen = () => {
    const pathsChosen = Object.values(
      this.props.onboarding.degreeChoices
    ).filter((degreeChoice) => {
      return degreeChoice.id;
    });
    return pathsChosen.length;
  };

  render() {
    if (this.state.loading) return <Loading height="100vh" />;

    return (
      <Translation>
        {(t) => (
          <>
            <WindowTitle>
              {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
              {t('Career Path')} - {t('Build your career path')}
            </WindowTitle>
            {this.state.showBrightOutlookModal && (
              <BrightOutlookModal
                tenant={this.props.tenant}
                onClickParent={this.setBrightOutlookModal}
              />
            )}
            {this.state.outOfDateData && <UpdateDataBanner />}
            <ModalController />
            <ScrollComponent />
            {/* --------------- Top Section --------------- */}
            <div
              className="careerpath-page"
              style={this.state.outOfDateData ? this.moveDown : {}}
            >
              <div
                className="top-section"
                style={this.borderTopSecondary}
                ref={this.careerPathRef}
              >
                <div className="top-row-routeback-login-container">
                  <RouteBack
                    color={this.props.tenant.primary}
                    route="/career-path/top-3"
                    tenant={this.props.tenant}
                    section="now let's build your career path!"
                    text="back"
                  />
                  <div className="replace-link-login-container">
                    <ReplaceCareerLink tenant={this.props.tenant} />
                    <LoginButton
                      tenant={this.props.tenant}
                      color={this.props.tenant.primary}
                      isAuthenticated={this.props.auth.isAuthenticated}
                      section="now let's build your career path"
                    />
                  </div>
                </div>
                <div className="columns logo-text-container">
                  <SchoolLogo
                    schoolLogoWidth="90%"
                    schoolLogoPadding="0 0 0 20px"
                    horizontalAlign="flex-start"
                  />
                  <div className="column is-full header-text-container">
                    <h1 className="main-header">
                      {t('Now let’s build your career path')}
                    </h1>
                    <h2>
                      {this.checkPathsChosen() !== 3
                        ? this.displayMobileDescription(t)
                        : t(
                            'Review your career and degree/credential choices.'
                          )}
                    </h2>
                    <hr
                      className="header-horizontal-line"
                      aria-hidden
                      style={this.headerHorizontalLineSecondary}
                    />
                  </div>
                </div>
              </div>
              {/* --------------- Middle Section --------------- */}
              <div className="middle-section" style={{ overflow: 'visible' }}>
                <CareerPathTabs
                  tenant={this.props.tenant}
                  // Pass value to child component
                  openList={this.state.openList}
                  // Pass ability to change that value to child component
                  isListOpen={this.isListOpen}
                  shortenButtonText={this.state.width < 985}
                  setBrightOutlookModal={this.setBrightOutlookModal}
                />
                {this.isListOpen()}
              </div>
              {/* --------------- Bottom Section --------------- */}
              <div className="bottom-section">
                <ProgressBar
                  bars={bars}
                  currentPhase={this.progressPercentage}
                  tenant={this.props.tenant}
                />
              </div>
              <CareerPathsBar
                tenant={this.props.tenant}
                pathsText={`${this.chosenPaths}/${this.totalPaths}`}
                isActive={this.chosenPaths === 3}
                onNextClick={() =>
                  this.handleNextButtonClicked(
                    this.props.tenant.name === 'InStride'
                      ? 'https://edassist.instride.com/programs'
                      : `/${this.props.tenant.name.toLowerCase()}/dashboard`
                  )
                }
              />
            </div>
          </>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  const ecap = getEcapState(state);

  return { onboarding, auth, ecap };
};

export default withRouter(
  connect(mapStateToProps, {
    getUserData,
    setEcap,
    getEcap,
    saveEcap,
    getData,
    getCurrentUserId,
  })(CareerPath)
);
